import { Component, HostListener, Inject, inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Router,
  NavigationEnd,
  RouterOutlet,
  RouterStateSnapshot,
  Event,
} from '@angular/router';
import { HeaderComponent } from './pages/header/header.component';
import { HttpClient } from '@angular/common/http';
import { AuthenticationStatus } from './models/core/types';
import { AuthService } from './shared/auth/auth.service';
import { isAllowedAnonymousLocation } from './helpers/common/routes-helper';
import { filter, map, Observable, Subject, takeUntil, tap } from 'rxjs';
import { AuthUserService } from './shared/auth/auth-user.service';

import { Roles } from './constants/roles';
import { SessionTimeoutService } from './services/session-timeout.service';
import {
  MsalService,
  MsalModule,
  MsalBroadcastService,
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration,
} from '@azure/msal-angular';

import {
  AuthenticationResult,
  InteractionStatus,
  PopupRequest,
  RedirectRequest,
  EventMessage,
  EventType,
} from '@azure/msal-browser';

import {
  KENDO_INDICATORS,
} from "@progress/kendo-angular-indicators";
import { NgxSpinnerModule } from "ngx-spinner";
import { NgxSpinnerService } from "ngx-spinner";

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, HeaderComponent, MsalModule, KENDO_INDICATORS, NgxSpinnerModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent {
  title = 'RiskPlatform.UI';
  isIframe = false;
  loginDisplay = false;
  private readonly _destroying$ = new Subject<void>();
  isAgentDownloadUrl: boolean = true;
  protected readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly authUserService = inject(AuthUserService);
  private readonly sessionTimeoutService = inject(SessionTimeoutService);
  private msalGuardConfig = inject(MSAL_GUARD_CONFIG);
  private authMsalService = inject(MsalService);
  private msalBroadcastService = inject(MsalBroadcastService);
  private spinner = inject(NgxSpinnerService);
  public showSplashScreen: boolean | undefined;
  public isUserSelected: boolean = false;

  ngOnInit() {
    // this.authMsalService.handleRedirectObservable().subscribe();
    this.isIframe = window !== window.parent && !window.opener;
    this.spinner.show();


    this.authService.signIn().subscribe({
      next: (result) => {
        if (isAllowedAnonymousLocation(window.location.href)) {
          this.showSplashScreen = false;
          return;
        }
        if (result === 1 || localStorage.getItem('loggedInUserGuidId')) {
          let loggedInUserRole = localStorage.getItem('loggedInUserRole');
          if (loggedInUserRole) {
            this.checkPermission(loggedInUserRole);
            this.authUserService.loggedInUserProfile.next(loggedInUserRole);
            this.sessionTimeoutService.startTimer();
          } else {
            let localStorageLoggedInId =
              localStorage.getItem('loggedInUserGuidId');
            let loggedInUserGuidId =
              localStorageLoggedInId ?? this.authService.getLoggedInUserId();

            this.authUserService
              .getCurrentUserDetails(loggedInUserGuidId)
              .then((res) => {
                this.authUserService.loggedInUserProfile.next(res.userRole);
                this.checkPermission(res.userRole);
                this.sessionTimeoutService.startTimer();
              })
              // .catch(() => {
              //   this.router.navigate(['/no-access']);
              // });
          }
        }
        this.authUserService
          .isUserAuthorizing()
          .pipe(
            filter(
              (status: AuthenticationStatus) =>
                status !== AuthenticationStatus.InProgress
            )
          )
          .subscribe({
            next: () => {
              this.showSplashScreen = false;
            },
            // error: () => {
            //   this.router.navigate(['/no-access']);
            //   this.showSplashScreen = false;
            // },
          });
      },
      // error: () => {
      //   this.router.navigate(['/no-access']);
      //   this.showSplashScreen = false;
      // },
    });
    this.router.events.pipe(
      filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      const exclusionPatterns = ['/GetDownloadURL/', '/GetSupportedFile/', '/UploadToStaging/'];
      const isDownloadUrl = exclusionPatterns.some(pattern => event.urlAfterRedirects.includes(pattern));
      
      if (isDownloadUrl) {
        this.spinner.hide();
        this.isAgentDownloadUrl = true;
      }
      else {
        this.isAgentDownloadUrl = false;
        this.spinner.hide();
      }
    });
    this.monitorUserActivity();
  }

  get showHeader(): boolean {
    return this.isUserSelected && this.router.url !== '/no-access';
  }

  private monitorUserActivity(): void {
    const resetTimer = () => {
      this.sessionTimeoutService.resetTimer();
    };

    window.addEventListener('click', resetTimer);
    window.addEventListener('keypress', resetTimer);
    window.addEventListener('mousemove', resetTimer);

    // Reset the timer on route change
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        resetTimer();
      }
    });
  }

  public checkPermission(userRole: string): void {
    if (
      userRole === Roles.GeneralUserRole ||
      userRole === Roles.SupportUserRole ||
      userRole === Roles.EngagementApproverUserRole
    ) {
      this.isUserSelected = true;
      this.spinner.show();
    } else {
      this.isUserSelected = false;
      this.spinner.hide();
      // this.router.navigate(['/no-access']);
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
